import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Package,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  BarChart3,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  MinusCircle,
  ArrowUpCircle,
  ArrowDownCircle,
  Calculator,
  Download,
  LineChart,
  Award,
  Wallet,
  Receipt,
  PiggyBank,
  Smartphone,
  Zap,
  Tv,
  CreditCard,
  Landmark,
  ShieldCheck,
  ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";
import { CurrentPositionTab } from "@/components/CurrentPositionTab";
import { LoansTab } from "@/components/LoansTab";
import { HowMuchDidIBuyTab } from "@/components/financial/HowMuchDidIBuyTab";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return `R ${(value || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (value) => {
  return `${(value || 0).toFixed(2)}%`;
};

export const FinancialSummary = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Overview month/year picker — allows navigating to previous months.
  const [overviewYear, setOverviewYear] = useState(new Date().getFullYear());
  const [overviewMonth, setOverviewMonth] = useState(new Date().getMonth() + 1);
  const nowYear = new Date().getFullYear();
  const nowMonth = new Date().getMonth() + 1;
  const isViewingCurrentMonth = overviewYear === nowYear && overviewMonth === nowMonth;
  
  // Sales Trends state
  const [salesTrends, setSalesTrends] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [trendsRange, setTrendsRange] = useState("12");
  const [compareYoY, setCompareYoY] = useState(false);
  const [productsSortBy, setProductsSortBy] = useState("profit");
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Turnover breakdown diagnostic modal
  const [showTurnoverBreakdown, setShowTurnoverBreakdown] = useState(false);
  const [breakdownData, setBreakdownData] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  useEffect(() => {
    if (!showTurnoverBreakdown) return;
    const periodYear = data?.period?.year;
    const periodMonth = data?.period?.month;
    if (!periodYear || !periodMonth) return;
    setLoadingBreakdown(true);
    setBreakdownData(null);
    axios
      .get(`${API}/financial-summary/turnover-breakdown`, { params: { year: periodYear, month: periodMonth } })
      .then((r) => setBreakdownData(r.data))
      .catch(() => setBreakdownData({ error: "Failed to load breakdown" }))
      .finally(() => setLoadingBreakdown(false));
  }, [showTurnoverBreakdown, data?.period?.year, data?.period?.month]);
  
  // Category & Department sales state
  const [categorySales, setCategorySales] = useState(null);
  const [departmentSales, setDepartmentSales] = useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("all");
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [loadingDepartment, setLoadingDepartment] = useState(false);
  
  // Net Profit state
  const [netProfitData, setNetProfitData] = useState(null);
  const [yearlyNetProfit, setYearlyNetProfit] = useState(null);
  const [loadingNetProfit, setLoadingNetProfit] = useState(false);
  const [netProfitYear, setNetProfitYear] = useState(new Date().getFullYear());
  const [netProfitMonth, setNetProfitMonth] = useState(new Date().getMonth() + 1);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/financial-summary/monthly`, {
        params: {
          year: overviewYear,
          month: overviewMonth,
          ...(showRefreshing ? { force_refresh: true } : {}),
        }
      });
      setData(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching financial summary:", error);
      if (showRefreshing) toast.error("Failed to load financial summary");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const fetchSalesTrends = async () => {
    setLoadingTrends(true);
    try {
      const response = await axios.get(`${API}/financial-summary/sales-trends`, {
        params: { date_range: trendsRange, compare_yoy: compareYoY }
      });
      setSalesTrends(response.data);
    } catch (error) {
      console.error("Error fetching sales trends:", error);
      toast.error("Failed to load sales trends");
    } finally {
      setLoadingTrends(false);
    }
  };
  
  const fetchTopProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await axios.get(`${API}/financial-summary/top-products`, {
        params: { date_range: trendsRange, sort_by: productsSortBy, limit: 20 }
      });
      setTopProducts(response.data);
    } catch (error) {
      console.error("Error fetching top products:", error);
      toast.error("Failed to load top products");
    } finally {
      setLoadingProducts(false);
    }
  };
  
  const fetchCategorySales = async () => {
    setLoadingCategory(true);
    try {
      const response = await axios.get(`${API}/financial-summary/sales-by-category`, {
        params: { date_range: trendsRange }
      });
      setCategorySales(response.data);
    } catch (error) {
      console.error("Error fetching category sales:", error);
    } finally {
      setLoadingCategory(false);
    }
  };
  
  const fetchDepartmentSales = async () => {
    setLoadingDepartment(true);
    try {
      const response = await axios.get(`${API}/financial-summary/sales-by-department`, {
        params: { 
          date_range: trendsRange,
          category: selectedCategoryFilter !== "all" ? selectedCategoryFilter : undefined
        }
      });
      setDepartmentSales(response.data);
    } catch (error) {
      console.error("Error fetching department sales:", error);
    } finally {
      setLoadingDepartment(false);
    }
  };
  
  const fetchNetProfit = async () => {
    setLoadingNetProfit(true);
    try {
      const [monthlyRes, yearlyRes] = await Promise.all([
        axios.get(`${API}/financial-summary/net-profit`, {
          params: { year: netProfitYear, month: netProfitMonth }
        }),
        axios.get(`${API}/financial-summary/net-profit/yearly`, {
          params: { year: netProfitYear }
        })
      ]);
      setNetProfitData(monthlyRes.data);
      setYearlyNetProfit(yearlyRes.data);
    } catch (error) {
      console.error("Error fetching net profit:", error);
      toast.error("Failed to load net profit data");
    } finally {
      setLoadingNetProfit(false);
    }
  };
  
  const exportSalesData = async () => {
    try {
      const response = await axios.get(`${API}/financial-summary/export-sales-data`, {
        params: { date_range: trendsRange }
      });
      
      // Convert to CSV
      const data = response.data.data;
      if (!data || data.length === 0) {
        toast.error("No data to export");
        return;
      }
      
      const headers = ["PLU Code", "Description", "Year", "Month", "Period", "Qty Sold", "Total Value", "Profit"];
      const csvContent = [
        headers.join(","),
        ...data.map(row => [
          `"${row.plu_code}"`,
          `"${(row.description || '').replace(/"/g, '""')}"`,
          row.year,
          row.month,
          `"${row.period_label}"`,
          row.qty_sold,
          row.total_value,
          row.total_profit
        ].join(","))
      ].join("\n");
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `sales_data_${trendsRange}months_${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      
      toast.success("Sales data exported successfully");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Failed to export data");
    }
  };

  // Initial load
  useEffect(() => {
    fetchData();
    fetchSalesTrends();
    fetchTopProducts();
    fetchCategorySales();
    fetchDepartmentSales();
    fetchNetProfit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Refetch trends when range or YoY toggle changes
  useEffect(() => {
    fetchSalesTrends();
    fetchTopProducts();
    fetchCategorySales();
    fetchDepartmentSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendsRange, compareYoY]);
  
  // Refetch products when sort changes
  useEffect(() => {
    fetchTopProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsSortBy]);
  
  // Refetch department sales when category filter changes
  useEffect(() => {
    fetchDepartmentSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryFilter]);
  
  // Refetch net profit when year/month changes
  useEffect(() => {
    fetchNetProfit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [netProfitYear, netProfitMonth]);

  // Refetch overview when overview month/year changes
  useEffect(() => {
    fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewYear, overviewMonth]);

  // Auto-refresh every 10 seconds when enabled AND viewing current month
  useEffect(() => {
    if (!autoRefresh || !isViewingCurrentMonth) return;
    
    const interval = setInterval(() => {
      fetchData(false); // Silent refresh
    }, 10000); // 10 seconds
    
    return () => clearInterval(interval);
  }, [autoRefresh, isViewingCurrentMonth, overviewYear, overviewMonth]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(true);
  };

  const handleCreateSnapshot = async () => {
    try {
      await axios.post(`${API}/financial-summary/create-snapshot`);
      toast.success("Stock snapshot created successfully");
      fetchData();
    } catch (error) {
      console.error("Error creating snapshot:", error);
      toast.error("Failed to create snapshot");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <p className="text-gray-600">Failed to load financial summary</p>
            <Button onClick={fetchData} className="mt-4">Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { stock_info, financial_info, period, snapshot_info, transaction_count, historical_sales, combined_totals, service_revenue } = data;

  // Profitability color based on score
  const getProfitabilityColor = (score) => {
    if (score >= 70) return "text-green-600";
    if (score >= 40) return "text-amber-600";
    return "text-red-600";
  };

  const getProfitabilityBg = (score) => {
    if (score >= 70) return "bg-green-100";
    if (score >= 40) return "bg-amber-100";
    return "bg-red-100";
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="financial-summary-title">Financial Summary</h1>
          <p className="text-gray-500">{period.month_name} {period.year}</p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">
              Last updated: {lastUpdated.toLocaleTimeString()}
              {autoRefresh && <span className="ml-2 text-green-600">• Auto-refresh ON</span>}
            </p>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-2 mr-2" data-testid="overview-month-picker">
            <span className="text-sm text-gray-600">Period:</span>
            <Select value={String(overviewMonth)} onValueChange={(v) => setOverviewMonth(Number(v))}>
              <SelectTrigger className="w-32 h-9 bg-white" data-testid="overview-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(overviewYear)} onValueChange={(v) => setOverviewYear(Number(v))}>
              <SelectTrigger className="w-24 h-9 bg-white" data-testid="overview-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 6 }, (_, i) => nowYear - i).map((yr) => (
                  <SelectItem key={yr} value={String(yr)}>{yr}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isViewingCurrentMonth && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 px-2 text-xs"
                onClick={() => { setOverviewYear(nowYear); setOverviewMonth(nowMonth); }}
                data-testid="overview-today-btn"
              >
                Today
              </Button>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 mr-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
              disabled={!isViewingCurrentMonth}
            />
            Auto-refresh
          </label>
          {!snapshot_info.snapshot_exists && (
            <Button variant="outline" onClick={handleCreateSnapshot} data-testid="create-snapshot-btn">
              <Calculator className="h-4 w-4 mr-2" />
              Create Snapshot
            </Button>
          )}
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Snapshot Warning */}
      {!snapshot_info.snapshot_exists && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">No Beginning Stock Snapshot</p>
              <p className="text-sm text-amber-700">Click the Create Snapshot button to capture the current stock value as the beginning value for this month.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 lg:w-[950px]">
          <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="overview-tab">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="net-profit" className="flex items-center gap-2" data-testid="net-profit-tab">
            <PiggyBank className="h-4 w-4" />
            Net Profit
          </TabsTrigger>
          <TabsTrigger value="current-position" className="flex items-center gap-2" data-testid="current-position-tab">
            <Landmark className="h-4 w-4" />
            Current Position
          </TabsTrigger>
          <TabsTrigger value="loans" className="flex items-center gap-2" data-testid="loans-tab-trigger">
            <CreditCard className="h-4 w-4" />
            Loans
          </TabsTrigger>
          <TabsTrigger value="how-much-did-i-buy" className="flex items-center gap-2" data-testid="how-much-did-i-buy-tab-trigger">
            <ShoppingCart className="h-4 w-4" />
            How Much Did I Buy
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab Content */}
        <TabsContent value="overview" className="space-y-6 mt-6">

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* STOCK INFO SECTION */}
        <Card className="lg:row-span-2">
          <CardHeader className="bg-blue-50 border-b">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Package className="h-5 w-5" />
              Stock Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Beginning Stock Value */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Beginning Stock Value</p>
                  <p className="text-sm text-gray-400">(1st of {period.month_name})</p>
                </div>
                <p className="text-2xl font-bold text-gray-800">
                  {formatCurrency(stock_info.beginning_stock_value)}
                </p>
              </div>
              {snapshot_info.snapshot_exists && (
                <p className="text-xs text-gray-400 mt-2">
                  Snapshot: {new Date(snapshot_info.snapshot_date).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Stock Purchases */}
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ArrowUpCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Stock Purchases (GRV)</p>
                    <p className="text-xs text-gray-400">Added this month</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-green-700">
                  +{formatCurrency(stock_info.stock_purchase_value)}
                </p>
              </div>
            </div>

            {/* Stock Adjustments */}
            <div className={`p-4 rounded-lg ${stock_info.stock_adjustment.total_value >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {stock_info.stock_adjustment.total_value >= 0 ? (
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                  ) : (
                    <MinusCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Stock Adjustments</p>
                    <p className="text-xs text-gray-400">(Stock Take / Write-offs)</p>
                  </div>
                </div>
                <p className={`text-2xl font-bold ${stock_info.stock_adjustment.total_value >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                  {stock_info.stock_adjustment.total_value >= 0 ? '+' : ''}{formatCurrency(stock_info.stock_adjustment.total_value)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Additions:</span>
                  <span className="text-green-600">+{formatCurrency(stock_info.stock_adjustment.additions)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Removals:</span>
                  <span className="text-red-600">-{formatCurrency(stock_info.stock_adjustment.removals)}</span>
                </div>
              </div>
              {stock_info.stock_adjustment.count > 0 && (
                <p className="text-xs text-gray-400 mt-2">{stock_info.stock_adjustment.count} adjustment(s) this month</p>
              )}
            </div>

            {/* End Stock Value */}
            <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Current Stock Value</p>
                  <p className="text-xs text-gray-400">(Real-time)</p>
                </div>
                <p className="text-2xl font-bold text-purple-700">
                  {formatCurrency(stock_info.end_stock_value)}
                </p>
              </div>
              <div className="mt-3 pt-3 border-t border-purple-200 flex justify-between items-center">
                <span className="text-sm text-gray-500">Stock Movement:</span>
                <span className={`font-semibold ${stock_info.stock_movement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stock_info.stock_movement >= 0 ? '+' : ''}{formatCurrency(stock_info.stock_movement)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FINANCIAL INFO SECTION */}
        <Card>
          <CardHeader className="bg-green-50 border-b">
            <CardTitle className="flex items-center gap-2 text-green-800 justify-between">
              <span className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Financial Information
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowTurnoverBreakdown(true)}
                  data-testid="turnover-breakdown-btn"
                  className="text-xs"
                >
                  Turnover Breakdown
                </Button>
                <Link
                  to={`/sales-audit?year=${overviewYear}&month=${overviewMonth}`}
                  data-testid="open-sales-audit-link"
                >
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                    Open Sales Audit
                  </Button>
                </Link>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Before Discounts */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">Before Discounts</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Turnover <span className="text-[10px] text-gray-400">(incl. VAT)</span></p>
                  <p className="text-lg font-bold text-gray-800">{formatCurrency(financial_info.turnover_before_discount)}</p>
                  {financial_info.turnover_before_discount_excl_vat !== undefined && (
                    <p className="text-[10px] text-gray-500 mt-0.5">≈ {formatCurrency(financial_info.turnover_before_discount_excl_vat)} excl. VAT</p>
                  )}
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Gross Profit <span className="text-[10px] text-gray-400">(excl. VAT)</span></p>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(financial_info.gross_profit_before_discount)}</p>
                </div>
              </div>
            </div>

            {/* Discounts Given */}
            <div className="p-3 bg-amber-50 rounded-lg flex justify-between items-center">
              <div className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-amber-600" />
                <span className="text-sm text-gray-600">Discounts Given</span>
              </div>
              <p className="text-lg font-bold text-amber-700">-{formatCurrency(financial_info.discount_given)}</p>
            </div>

            {/* After Discounts */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">After Discounts</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-500">Turnover <span className="text-[10px] text-gray-400">(incl. VAT)</span></p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(financial_info.turnover_after_discount)}</p>
                  {financial_info.turnover_after_discount_excl_vat !== undefined && (
                    <p className="text-[10px] text-blue-600/70 mt-0.5">≈ {formatCurrency(financial_info.turnover_after_discount_excl_vat)} excl. VAT</p>
                  )}
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <p className="text-xs text-gray-500">Gross Profit <span className="text-[10px] text-gray-400">(excl. VAT)</span></p>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(financial_info.gross_profit_after_discount)}</p>
                </div>
              </div>
            </div>

            {/* GP Percentages */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-xs text-gray-500">GP% Before Discount</p>
                <p className="text-xl font-bold text-gray-700">{formatPercent(financial_info.gp_percent_before_discount)}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-xs text-gray-500">GP% After Discount</p>
                <p className="text-xl font-bold text-green-700">{formatPercent(financial_info.gp_percent_after_discount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <Card>
          <CardHeader className="bg-purple-50 border-b">
            <CardTitle className="flex items-center gap-2 text-purple-800">
              <BarChart3 className="h-5 w-5" />
              Key Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Inventory Turnover Rate */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Inventory Turnover Rate</p>
                  <p className="text-xs text-gray-400">COGS ÷ Avg Stock Value</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-800">{financial_info.inventory_turnover_rate.toFixed(2)}x</p>
                  <p className="text-xs text-gray-400">times this month</p>
                </div>
              </div>
            </div>

            {/* Return on Stock Investment */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Return on Stock Investment</p>
                  <p className="text-xs text-gray-400">Gross Profit ÷ Stock Value</p>
                </div>
                <p className="text-2xl font-bold text-blue-700">{formatPercent(financial_info.roi_percentage)}</p>
              </div>
            </div>

            {/* Gross Profit Margin (Standard) */}
            <div className="p-4 rounded-lg bg-emerald-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Gross Profit Margin</p>
                  <p className="text-xs text-gray-400">(Revenue - COGS) ÷ Revenue</p>
                </div>
                <p className="text-2xl font-bold text-emerald-700">{formatPercent(financial_info.gross_profit_margin || financial_info.gp_percent_after_discount)}</p>
              </div>
            </div>

            {/* Profitability Score (0-100) */}
            <div className={`p-4 rounded-lg ${getProfitabilityBg(financial_info.profitability_score)}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm text-gray-600">Profitability Score</p>
                  <p className="text-xs text-gray-400">Business Performance (0-100)</p>
                </div>
                <p className={`text-3xl font-bold ${getProfitabilityColor(financial_info.profitability_score)}`}>
                  {financial_info.profitability_score.toFixed(0)}
                </p>
              </div>
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${
                    financial_info.profitability_score >= 70 ? 'bg-green-500' :
                    financial_info.profitability_score >= 40 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${financial_info.profitability_score}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Low</span>
                <span>Average</span>
                <span>Excellent</span>
              </div>
            </div>

            {/* Transaction Summary */}
            <div className="p-3 bg-blue-50 rounded-lg flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Transactions</span>
              <span className="text-lg font-bold text-blue-700">{transaction_count}</span>
            </div>

            {/* Cost of Goods Sold */}
            <div className="p-3 bg-gray-50 rounded-lg flex justify-between items-center">
              <span className="text-sm text-gray-600">Cost of Goods Sold</span>
              <span className="text-lg font-bold text-gray-700">{formatCurrency(financial_info.cost_of_goods_sold)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Service Revenue Section */}
      {service_revenue && service_revenue.total_revenue > 0 && (
        <Card className="border-cyan-200">
          <CardHeader className="bg-cyan-50 border-b">
            <CardTitle className="flex items-center gap-2 text-cyan-800">
              <Smartphone className="h-5 w-5" />
              Service Revenue (Pass-Through Sales)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-cyan-50 rounded-lg text-center">
                <p className="text-xs text-gray-500 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-cyan-700">{formatCurrency(service_revenue.total_revenue)}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-xs text-gray-500 mb-1">Cost (Pass-Through)</p>
                <p className="text-2xl font-bold text-gray-700">{formatCurrency(service_revenue.total_cost)}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-xs text-gray-500 mb-1">Service Profit</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(service_revenue.profit)}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-gray-500 mb-1">Transactions</p>
                <p className="text-2xl font-bold text-blue-700">{service_revenue.transaction_count}</p>
              </div>
            </div>
            
            {/* Service Breakdown */}
            {service_revenue.breakdown && Object.keys(service_revenue.breakdown).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">Breakdown by Service Type</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(service_revenue.breakdown).map(([type, data]) => {
                    const icons = {
                      airtime: <Smartphone className="h-4 w-4" />,
                      electricity: <Zap className="h-4 w-4" />,
                      dstv: <Tv className="h-4 w-4" />,
                      voucher: <CreditCard className="h-4 w-4" />,
                      other: <Receipt className="h-4 w-4" />
                    };
                    const colors = {
                      airtime: "bg-blue-50 border-blue-200",
                      electricity: "bg-yellow-50 border-yellow-200",
                      dstv: "bg-purple-50 border-purple-200",
                      voucher: "bg-pink-50 border-pink-200",
                      other: "bg-gray-50 border-gray-200"
                    };
                    return (
                      <div key={type} className={`p-3 rounded-lg border ${colors[type] || colors.other}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {icons[type] || icons.other}
                          <span className="font-medium capitalize">{type}</span>
                        </div>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Revenue:</span>
                            <span className="font-semibold">{formatCurrency(data.revenue)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Profit:</span>
                            <span className="font-semibold text-green-600">{formatCurrency(data.profit)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Count:</span>
                            <span className="font-semibold">{data.count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Historical Sales Section - Full Width */}
      {historical_sales && (
        <Card className={historical_sales.has_data ? "border-amber-200" : "border-gray-200"}>
          <CardHeader className={historical_sales.has_data ? "bg-amber-50 border-b" : "bg-gray-50 border-b"}>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <TrendingUp className="h-5 w-5" />
              Historical Sales Data (Imported)
              {!historical_sales.has_data && (
                <span className="text-xs font-normal text-gray-500 ml-2">
                  No data imported yet
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {historical_sales.has_data ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* All Time Historical Data */}
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-amber-800 mb-4 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    All-Time Historical Sales
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Sales Value</span>
                      <span className="text-xl font-bold text-amber-700">
                        {formatCurrency(historical_sales.all_time.total_value)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Quantity Sold</span>
                      <span className="text-lg font-semibold text-gray-700">
                        {historical_sales.all_time.total_qty.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Transaction Count</span>
                      <span className="text-lg font-semibold text-gray-700">
                        {historical_sales.all_time.transaction_count.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Products with History</span>
                      <span className="text-lg font-semibold text-gray-700">
                        {historical_sales.all_time.product_count}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Combined Totals */}
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-green-800 mb-4 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Combined Business Totals
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Live Sales (This Period)</span>
                      <span className="text-lg font-semibold text-blue-700">
                        {formatCurrency(financial_info.turnover_after_discount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Historical Sales (Imported)</span>
                      <span className="text-lg font-semibold text-amber-700">
                        {formatCurrency(historical_sales.all_time.total_value)}
                      </span>
                    </div>
                    <div className="border-t pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Total Combined Turnover</span>
                        <span className="text-xl font-bold text-green-700">
                          {formatCurrency(combined_totals?.turnover || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-sm text-gray-600">Total Transactions</span>
                        <span className="text-lg font-semibold text-gray-700">
                          {(combined_totals?.transaction_count || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No historical sales data has been imported yet.</p>
                <p className="text-sm text-gray-400 mt-2">
                  You can import historical sales data from PDF or Excel files via the Import page.
                </p>
                <Button variant="outline" className="mt-4" onClick={() => window.location.href = '/import'}>
                  Go to Import
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sales Trends & Analytics Section */}
      <Card className="border-indigo-200">
        <CardHeader className="bg-indigo-50 border-b">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="flex items-center gap-2 text-indigo-800">
              <LineChart className="h-5 w-5" />
              Sales Trends & Analytics
            </CardTitle>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Range Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Period:</span>
                <Select value={trendsRange} onValueChange={setTrendsRange}>
                  <SelectTrigger className="w-32 h-8 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">Last 6 Months</SelectItem>
                    <SelectItem value="12">Last 12 Months</SelectItem>
                    <SelectItem value="24">Last 24 Months</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* YoY Toggle */}
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={compareYoY}
                  onChange={(e) => setCompareYoY(e.target.checked)}
                  className="rounded"
                />
                Compare Year-over-Year
              </label>
              
              {/* Export Button */}
              <Button variant="outline" size="sm" onClick={exportSalesData} className="h-8">
                <Download className="h-4 w-4 mr-1" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {loadingTrends ? (
            <div className="h-80 flex items-center justify-center">
              <RefreshCw className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          ) : salesTrends && salesTrends.trends.length > 0 ? (
            <div className="space-y-8">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-indigo-50 rounded-lg text-center">
                  <p className="text-sm text-indigo-600">Total Sales</p>
                  <p className="text-2xl font-bold text-indigo-800">{formatCurrency(salesTrends.summary.total_sales)}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-sm text-green-600">Total Profit</p>
                  <p className="text-2xl font-bold text-green-800">{formatCurrency(salesTrends.summary.total_profit)}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-sm text-amber-600">Avg Monthly Sales</p>
                  <p className="text-2xl font-bold text-amber-800">{formatCurrency(salesTrends.summary.avg_monthly_sales)}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-sm text-blue-600">Overall Margin</p>
                  <p className="text-2xl font-bold text-blue-800">{formatPercent(salesTrends.summary.overall_margin)}</p>
                </div>
              </div>

              {/* Sales Trend Line Chart */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Monthly Sales & Profit Trend</h4>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={salesTrends.trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="period_label" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `R${(value / 1000).toFixed(0)}k`}
                      />
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip 
                        formatter={(value, name, props) => {
                          const dataKey = props?.dataKey;
                          if (dataKey === 'margin_percent' || name === 'Margin %') {
                            return [`${value}%`, 'Margin'];
                          }
                          if (dataKey === 'total_sales' || name === 'Sales') {
                            return [`R ${value.toLocaleString()}`, 'Sales'];
                          }
                          if (dataKey === 'total_profit' || name === 'Profit') {
                            return [`R ${value.toLocaleString()}`, 'Profit'];
                          }
                          return [`R ${value.toLocaleString()}`, name];
                        }}
                        labelStyle={{ fontWeight: 'bold' }}
                      />
                      <Legend />
                      <Bar 
                        yAxisId="left"
                        dataKey="total_sales" 
                        name="Sales" 
                        fill="#6366f1" 
                        opacity={0.7}
                        radius={[4, 4, 0, 0]}
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="total_profit" 
                        name="Profit" 
                        stroke="#22c55e" 
                        strokeWidth={3}
                        dot={{ fill: '#22c55e', strokeWidth: 2 }}
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="margin_percent" 
                        name="Margin %" 
                        stroke="#f59e0b" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Year-over-Year Comparison */}
              {compareYoY && salesTrends.yoy_comparison && salesTrends.yoy_comparison.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Year-over-Year Comparison</h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={salesTrends.yoy_comparison}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="month_name" 
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis 
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) => `R${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          formatter={(value, name) => [`R ${value.toLocaleString()}`, name]}
                        />
                        <Legend />
                        <Bar 
                          dataKey="previous_sales" 
                          name={`${salesTrends.yoy_comparison[0]?.previous_year || ''} Sales`}
                          fill="#94a3b8"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar 
                          dataKey="current_sales" 
                          name={`${salesTrends.yoy_comparison[0]?.current_year || ''} Sales`}
                          fill="#6366f1"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-80 flex flex-col items-center justify-center text-gray-500">
              <BarChart3 className="h-12 w-12 text-gray-300 mb-3" />
              <p>No sales trend data available for the selected period.</p>
              <p className="text-sm text-gray-400 mt-1">Import monthly sales data to see trends.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category & Department Sales Section */}
      <Card className="border-orange-200">
        <CardHeader className="bg-orange-50 border-b">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <BarChart3 className="h-5 w-5" />
              Sales by Category & Department
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Filter Department:</span>
              <Select value={selectedCategoryFilter} onValueChange={setSelectedCategoryFilter}>
                <SelectTrigger className="w-40 h-8 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {[...(categorySales?.categories || [])].sort((a, b) => (a.category || '').localeCompare(b.category || '')).map(cat => (
                    <SelectItem key={cat.category} value={cat.category}>{cat.category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Category Sales */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-4">Sales by Category</h4>
              {loadingCategory ? (
                <div className="h-80 flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 animate-spin text-orange-500" />
                </div>
              ) : categorySales && categorySales.categories.length > 0 ? (
                <div className="space-y-4">
                  {/* Pie Chart */}
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categorySales.categories.slice(0, 8)}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="total_sales"
                          nameKey="category"
                          label={({ category, sales_percent }) => `${category.substring(0, 10)}${category.length > 10 ? '..' : ''} (${sales_percent}%)`}
                          labelLine={false}
                        >
                          {categorySales.categories.slice(0, 8).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={[
                              '#f97316', '#fb923c', '#fdba74', '#fed7aa',
                              '#0ea5e9', '#38bdf8', '#7dd3fc', '#bae6fd'
                            ][index % 8]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => [`R ${value.toLocaleString()}`, 'Sales']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Category Table */}
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b">
                          <th className="text-left py-2 px-1 font-medium text-gray-600">Category</th>
                          <th className="text-right py-2 px-1 font-medium text-gray-600">Sales</th>
                          <th className="text-right py-2 px-1 font-medium text-gray-600">Profit</th>
                          <th className="text-right py-2 px-1 font-medium text-gray-600">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categorySales.categories.map((cat, i) => (
                          <tr 
                            key={cat.category} 
                            className={`border-b hover:bg-orange-50 cursor-pointer ${selectedCategoryFilter === cat.category ? 'bg-orange-100' : ''}`}
                            onClick={() => setSelectedCategoryFilter(cat.category)}
                          >
                            <td className="py-2 px-1">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: ['#f97316', '#fb923c', '#fdba74', '#fed7aa', '#0ea5e9', '#38bdf8', '#7dd3fc', '#bae6fd'][i % 8] }}
                                />
                                <span className="truncate max-w-[120px]" title={cat.category}>{cat.category}</span>
                              </div>
                            </td>
                            <td className="text-right py-2 px-1 font-medium">{formatCurrency(cat.total_sales)}</td>
                            <td className="text-right py-2 px-1 text-green-700">{formatCurrency(cat.total_profit)}</td>
                            <td className={`text-right py-2 px-1 ${cat.margin_percent >= 20 ? 'text-green-600' : cat.margin_percent >= 10 ? 'text-amber-600' : 'text-red-600'}`}>
                              {cat.margin_percent.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="h-80 flex flex-col items-center justify-center text-gray-500">
                  <BarChart3 className="h-10 w-10 text-gray-300 mb-2" />
                  <p>No category data available</p>
                </div>
              )}
            </div>
            
            {/* Department Sales */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-4">
                Sales by Department
                {selectedCategoryFilter !== "all" && (
                  <span className="ml-2 text-xs font-normal text-orange-600">
                    (Filtered: {selectedCategoryFilter})
                  </span>
                )}
              </h4>
              {loadingDepartment ? (
                <div className="h-80 flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 animate-spin text-orange-500" />
                </div>
              ) : departmentSales && departmentSales.departments.length > 0 ? (
                <div className="space-y-4">
                  {/* Horizontal Bar Chart */}
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={departmentSales.departments.slice(0, 10)} 
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                        <XAxis 
                          type="number" 
                          tick={{ fontSize: 10 }}
                          tickFormatter={(value) => `R${(value / 1000).toFixed(0)}k`}
                        />
                        <YAxis 
                          type="category" 
                          dataKey="department" 
                          tick={{ fontSize: 10 }}
                          width={100}
                          tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '..' : value}
                        />
                        <Tooltip 
                          formatter={(value, name) => [
                            `R ${value.toLocaleString()}`, 
                            name === 'total_sales' ? 'Sales' : 'Profit'
                          ]}
                        />
                        <Bar dataKey="total_sales" name="Sales" fill="#f97316" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Department Table */}
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b">
                          <th className="text-left py-2 px-1 font-medium text-gray-600">Department</th>
                          <th className="text-right py-2 px-1 font-medium text-gray-600">Sales</th>
                          <th className="text-right py-2 px-1 font-medium text-gray-600">%</th>
                          <th className="text-right py-2 px-1 font-medium text-gray-600">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {departmentSales.departments.slice(0, 15).map((dept) => (
                          <tr key={`${dept.category}-${dept.department}`} className="border-b hover:bg-orange-50">
                            <td className="py-2 px-1">
                              <div className="truncate max-w-[150px]" title={`${dept.category} > ${dept.department}`}>
                                {selectedCategoryFilter === "all" && (
                                  <span className="text-xs text-gray-400">{dept.category} &gt; </span>
                                )}
                                {dept.department}
                              </div>
                            </td>
                            <td className="text-right py-2 px-1 font-medium">{formatCurrency(dept.total_sales)}</td>
                            <td className="text-right py-2 px-1 text-gray-500">{dept.sales_percent.toFixed(1)}%</td>
                            <td className={`text-right py-2 px-1 ${dept.margin_percent >= 20 ? 'text-green-600' : dept.margin_percent >= 10 ? 'text-amber-600' : 'text-red-600'}`}>
                              {dept.margin_percent.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="h-80 flex flex-col items-center justify-center text-gray-500">
                  <BarChart3 className="h-10 w-10 text-gray-300 mb-2" />
                  <p>No department data available</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Summary Stats */}
          {categorySales && (
            <div className="mt-6 pt-4 border-t grid grid-cols-3 gap-4">
              <div className="p-3 bg-orange-50 rounded-lg text-center">
                <p className="text-xs text-orange-600">Total Categories</p>
                <p className="text-xl font-bold text-orange-800">{categorySales.summary.category_count}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-blue-600">Total Sales</p>
                <p className="text-xl font-bold text-blue-800">{formatCurrency(categorySales.summary.total_sales)}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-xs text-green-600">Total Profit</p>
                <p className="text-xl font-bold text-green-800">{formatCurrency(categorySales.summary.total_profit)}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Products Section */}
      <Card className="border-green-200">
        <CardHeader className="bg-green-50 border-b">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="flex items-center gap-2 text-green-800">
              <Award className="h-5 w-5" />
              Top Performing Products
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Sort by:</span>
              <Select value={productsSortBy} onValueChange={setProductsSortBy}>
                <SelectTrigger className="w-28 h-8 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="profit">Profit</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="qty">Quantity</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {loadingProducts ? (
            <div className="h-64 flex items-center justify-center">
              <RefreshCw className="h-8 w-8 animate-spin text-green-500" />
            </div>
          ) : topProducts && topProducts.products.length > 0 ? (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-green-600">Total {productsSortBy === 'profit' ? 'Profit' : productsSortBy === 'sales' ? 'Sales' : 'Qty'} (Top 20)</p>
                  <p className="text-xl font-bold text-green-800">
                    {productsSortBy === 'qty' 
                      ? topProducts.products.reduce((sum, p) => sum + p.total_qty, 0).toLocaleString()
                      : formatCurrency(topProducts.total_in_results[productsSortBy === 'profit' ? 'profit' : 'sales'])
                    }
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600">These products represent</p>
                  <p className="text-xl font-bold text-blue-800">
                    {topProducts.products.reduce((sum, p) => sum + (productsSortBy === 'profit' ? p.profit_percent : p.sales_percent), 0).toFixed(1)}%
                  </p>
                  <p className="text-xs text-gray-500">of total {productsSortBy === 'profit' ? 'profit' : 'sales'}</p>
                </div>
              </div>

              {/* Products Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-2 font-medium text-gray-600">#</th>
                      <th className="text-left py-2 px-2 font-medium text-gray-600">PLU</th>
                      <th className="text-left py-2 px-2 font-medium text-gray-600">Product</th>
                      <th className="text-right py-2 px-2 font-medium text-gray-600">Sales</th>
                      <th className="text-right py-2 px-2 font-medium text-gray-600">Profit</th>
                      <th className="text-right py-2 px-2 font-medium text-gray-600">Margin</th>
                      <th className="text-right py-2 px-2 font-medium text-gray-600">Qty Sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducts.products.map((product, index) => (
                      <tr 
                        key={product.plu_code} 
                        className={`border-b border-gray-100 ${index < 3 ? 'bg-green-50' : ''}`}
                      >
                        <td className="py-2 px-2 font-medium text-gray-600">
                          {index < 3 ? (
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white ${
                              index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-amber-600'
                            }`}>
                              {index + 1}
                            </span>
                          ) : (
                            index + 1
                          )}
                        </td>
                        <td className="py-2 px-2 text-gray-600 font-mono text-xs">{product.plu_code}</td>
                        <td className="py-2 px-2 text-gray-800 max-w-xs truncate" title={product.description}>
                          {product.description}
                        </td>
                        <td className="py-2 px-2 text-right font-semibold text-gray-800">
                          {formatCurrency(product.total_sales)}
                          <span className="text-xs text-gray-500 ml-1">({product.sales_percent}%)</span>
                        </td>
                        <td className="py-2 px-2 text-right font-semibold text-green-700">
                          {formatCurrency(product.total_profit)}
                          <span className="text-xs text-gray-500 ml-1">({product.profit_percent}%)</span>
                        </td>
                        <td className={`py-2 px-2 text-right font-medium ${
                          product.margin_percent >= 20 ? 'text-green-600' : 
                          product.margin_percent >= 10 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {formatPercent(product.margin_percent)}
                        </td>
                        <td className="py-2 px-2 text-right text-gray-600">
                          {product.total_qty.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-500">
              <Award className="h-12 w-12 text-gray-300 mb-3" />
              <p>No product data available for the selected period.</p>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Net Profit Tab Content */}
        <TabsContent value="net-profit" className="space-y-6 mt-6">
          {/* Net Profit Period Selectors */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Year:</span>
              <Select value={String(netProfitYear)} onValueChange={(v) => setNetProfitYear(Number(v))}>
                <SelectTrigger className="w-28 h-9 bg-white" data-testid="net-profit-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(5)].map((_, i) => {
                    const year = new Date().getFullYear() - i;
                    return <SelectItem key={year} value={String(year)}>{year}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Month:</span>
              <Select value={String(netProfitMonth)} onValueChange={(v) => setNetProfitMonth(Number(v))}>
                <SelectTrigger className="w-36 h-9 bg-white" data-testid="net-profit-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={fetchNetProfit} disabled={loadingNetProfit} data-testid="refresh-net-profit-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loadingNetProfit ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loadingNetProfit ? (
            <div className="h-64 flex items-center justify-center">
              <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : netProfitData ? (
            <div className="space-y-6">
              {/* Net Profit Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {/* Revenue */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Receipt className="h-5 w-5 text-blue-600" />
                    </div>
                    <p className="text-xs text-blue-600 font-medium">Revenue</p>
                    <p className="text-xl font-bold text-blue-800" data-testid="net-profit-revenue">
                      {formatCurrency(netProfitData.revenue?.total || 0)}
                    </p>
                    <p className="text-xs text-blue-500 mt-1">
                      {netProfitData.revenue?.transaction_count || 0} transactions
                    </p>
                  </CardContent>
                </Card>

                {/* COGS */}
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Package className="h-5 w-5 text-amber-600" />
                    </div>
                    <p className="text-xs text-amber-600 font-medium">Cost of Goods Sold</p>
                    <p className="text-xl font-bold text-amber-800" data-testid="net-profit-cogs">
                      {formatCurrency(netProfitData.cost_of_goods_sold?.total || 0)}
                    </p>
                  </CardContent>
                </Card>

                {/* Gross Profit */}
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="text-xs text-green-600 font-medium">Gross Profit</p>
                    <p className="text-xl font-bold text-green-800" data-testid="net-profit-gross">
                      {formatCurrency(netProfitData.gross_profit?.total || 0)}
                    </p>
                    <p className="text-xs text-green-500 mt-1">
                      {formatPercent(netProfitData.gross_profit?.margin_percent || 0)} margin
                    </p>
                  </CardContent>
                </Card>

                {/* Operating Expenses */}
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Wallet className="h-5 w-5 text-red-600" />
                    </div>
                    <p className="text-xs text-red-600 font-medium">Operating Expenses</p>
                    <p className="text-xl font-bold text-red-800" data-testid="net-profit-expenses">
                      {formatCurrency(netProfitData.operating_expenses?.total || 0)}
                    </p>
                    <p className="text-xs text-red-500 mt-1">
                      {netProfitData.operating_expenses?.count || 0} entries
                    </p>
                  </CardContent>
                </Card>

                {/* Net Profit */}
                <Card className={`${netProfitData.net_profit?.is_profitable ? 'bg-emerald-50 border-emerald-200' : 'bg-red-100 border-red-300'}`}>
                  <CardContent className="p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <PiggyBank className={`h-5 w-5 ${netProfitData.net_profit?.is_profitable ? 'text-emerald-600' : 'text-red-600'}`} />
                    </div>
                    <p className={`text-xs font-medium ${netProfitData.net_profit?.is_profitable ? 'text-emerald-600' : 'text-red-600'}`}>
                      Net Profit
                    </p>
                    <p className={`text-2xl font-bold ${netProfitData.net_profit?.is_profitable ? 'text-emerald-800' : 'text-red-800'}`} data-testid="net-profit-total">
                      {formatCurrency(netProfitData.net_profit?.total || 0)}
                    </p>
                    <p className={`text-xs mt-1 ${netProfitData.net_profit?.is_profitable ? 'text-emerald-500' : 'text-red-500'}`}>
                      {formatPercent(netProfitData.net_profit?.margin_percent || 0)} margin
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Net Profit Breakdown Card */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Expense Breakdown */}
                <Card className="border-red-200">
                  <CardHeader className="bg-red-50 border-b">
                    <CardTitle className="flex items-center gap-2 text-red-800 text-lg">
                      <Wallet className="h-5 w-5" />
                      Expense Breakdown - {netProfitData.period?.month_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    {netProfitData.operating_expenses?.breakdown?.length > 0 ? (
                      <div className="space-y-4">
                        {/* Pie Chart for Expenses */}
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={netProfitData.operating_expenses.breakdown}
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={70}
                                paddingAngle={2}
                                dataKey="amount"
                                nameKey="category_name"
                                label={({ category_name, percentage }) => `${(category_name || 'Unknown').substring(0, 8)}${(category_name || '').length > 8 ? '..' : ''} (${percentage}%)`}
                                labelLine={false}
                              >
                                {netProfitData.operating_expenses.breakdown.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={[
                                    '#ef4444', '#f97316', '#eab308', '#22c55e',
                                    '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'
                                  ][index % 8]} />
                                ))}
                              </Pie>
                              <Tooltip 
                                formatter={(value) => [`R ${value.toLocaleString()}`, 'Amount']}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        
                        {/* Expense Table */}
                        <div className="max-h-48 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-white">
                              <tr className="border-b">
                                <th className="text-left py-2 px-1 font-medium text-gray-600">Category</th>
                                <th className="text-right py-2 px-1 font-medium text-gray-600">Amount</th>
                                <th className="text-right py-2 px-1 font-medium text-gray-600">%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {netProfitData.operating_expenses.breakdown.map((exp, i) => (
                                <tr key={exp.category_id || i} className="border-b hover:bg-red-50">
                                  <td className="py-2 px-1 flex items-center gap-2">
                                    <div 
                                      className="w-3 h-3 rounded-full" 
                                      style={{ backgroundColor: ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'][i % 8] }}
                                    />
                                    {exp.category_name || 'Unknown'}
                                  </td>
                                  <td className="text-right py-2 px-1 font-medium">{formatCurrency(exp.amount)}</td>
                                  <td className="text-right py-2 px-1 text-gray-500">{exp.percentage}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="h-48 flex flex-col items-center justify-center text-gray-500">
                        <Wallet className="h-10 w-10 text-gray-300 mb-2" />
                        <p>No expenses recorded for this period</p>
                        <Button variant="link" className="mt-2 text-sm" onClick={() => window.location.href = '/expenses'}>
                          Add Expenses
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Net Profit Formula / Visualization */}
                <Card className="border-emerald-200">
                  <CardHeader className="bg-emerald-50 border-b">
                    <CardTitle className="flex items-center gap-2 text-emerald-800 text-lg">
                      <Calculator className="h-5 w-5" />
                      Profit Calculation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      {/* Visual Formula */}
                      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Revenue</span>
                          <span className="font-semibold text-blue-700">{formatCurrency(netProfitData.revenue?.total || 0)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">− Cost of Goods Sold</span>
                          <span className="font-semibold text-amber-700">−{formatCurrency(netProfitData.cost_of_goods_sold?.total || 0)}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between items-center">
                          <span className="text-gray-700 font-medium">= Gross Profit</span>
                          <span className="font-bold text-green-700">{formatCurrency(netProfitData.gross_profit?.total || 0)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">− Operating Expenses</span>
                          <span className="font-semibold text-red-700">−{formatCurrency(netProfitData.operating_expenses?.total || 0)}</span>
                        </div>
                        <div className="border-t-2 border-emerald-300 pt-3 flex justify-between items-center">
                          <span className="text-emerald-800 font-bold text-lg">= Net Profit</span>
                          <span className={`font-bold text-xl ${netProfitData.net_profit?.is_profitable ? 'text-emerald-700' : 'text-red-700'}`}>
                            {formatCurrency(netProfitData.net_profit?.total || 0)}
                          </span>
                        </div>
                      </div>

                      {/* Profit Status */}
                      <div className={`p-4 rounded-lg ${netProfitData.net_profit?.is_profitable ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        <div className="flex items-center gap-3">
                          {netProfitData.net_profit?.is_profitable ? (
                            <CheckCircle className="h-8 w-8 text-emerald-600" />
                          ) : (
                            <AlertTriangle className="h-8 w-8 text-red-600" />
                          )}
                          <div>
                            <p className={`font-bold ${netProfitData.net_profit?.is_profitable ? 'text-emerald-800' : 'text-red-800'}`}>
                              {netProfitData.net_profit?.is_profitable ? 'Profitable' : 'Operating at a Loss'}
                            </p>
                            <p className={`text-sm ${netProfitData.net_profit?.is_profitable ? 'text-emerald-600' : 'text-red-600'}`}>
                              Net margin: {formatPercent(netProfitData.net_profit?.margin_percent || 0)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Yearly Trend Chart */}
              {yearlyNetProfit && (
                <Card className="border-indigo-200">
                  <CardHeader className="bg-indigo-50 border-b">
                    <CardTitle className="flex items-center gap-2 text-indigo-800">
                      <LineChart className="h-5 w-5" />
                      {yearlyNetProfit.year} Monthly Net Profit Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-6">
                      {/* YTD Summary */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                          <p className="text-xs text-blue-600">YTD Revenue</p>
                          <p className="text-lg font-bold text-blue-800">{formatCurrency(yearlyNetProfit.year_to_date.revenue)}</p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <p className="text-xs text-green-600">YTD Gross Profit</p>
                          <p className="text-lg font-bold text-green-800">{formatCurrency(yearlyNetProfit.year_to_date.gross_profit)}</p>
                        </div>
                        <div className="p-3 bg-red-50 rounded-lg text-center">
                          <p className="text-xs text-red-600">YTD Expenses</p>
                          <p className="text-lg font-bold text-red-800">{formatCurrency(yearlyNetProfit.year_to_date.expenses)}</p>
                        </div>
                        <div className={`p-3 rounded-lg text-center ${yearlyNetProfit.year_to_date.net_profit >= 0 ? 'bg-emerald-50' : 'bg-red-100'}`}>
                          <p className={`text-xs ${yearlyNetProfit.year_to_date.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>YTD Net Profit</p>
                          <p className={`text-lg font-bold ${yearlyNetProfit.year_to_date.net_profit >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                            {formatCurrency(yearlyNetProfit.year_to_date.net_profit)}
                          </p>
                        </div>
                      </div>

                      {/* Monthly Chart */}
                      <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={yearlyNetProfit.monthly_data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis 
                              dataKey="month_name" 
                              tick={{ fontSize: 11 }}
                            />
                            <YAxis 
                              tick={{ fontSize: 11 }}
                              tickFormatter={(value) => `R${(value / 1000).toFixed(0)}k`}
                            />
                            <Tooltip 
                              formatter={(value, name) => {
                                const label = name === 'revenue' ? 'Revenue' : 
                                             name === 'expenses' ? 'Expenses' :
                                             name === 'gross_profit' ? 'Gross Profit' :
                                             name === 'net_profit' ? 'Net Profit' : name;
                                return [`R ${value.toLocaleString()}`, label];
                              }}
                            />
                            <Legend />
                            <Bar 
                              dataKey="revenue" 
                              name="Revenue" 
                              fill="#3b82f6" 
                              opacity={0.4}
                              radius={[4, 4, 0, 0]}
                            />
                            <Bar 
                              dataKey="expenses" 
                              name="Expenses" 
                              fill="#ef4444" 
                              opacity={0.4}
                              radius={[4, 4, 0, 0]}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="gross_profit" 
                              name="Gross Profit" 
                              stroke="#22c55e" 
                              strokeWidth={2}
                              dot={{ fill: '#22c55e', strokeWidth: 2 }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="net_profit" 
                              name="Net Profit" 
                              stroke="#10b981" 
                              strokeWidth={3}
                              dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-500">
              <PiggyBank className="h-12 w-12 text-gray-300 mb-3" />
              <p>No net profit data available</p>
            </div>
          )}
        </TabsContent>

        {/* Current Position Tab */}
        <TabsContent value="current-position" className="space-y-6 mt-6">
          <CurrentPositionTab />
        </TabsContent>

        {/* Loans Tab */}
        <TabsContent value="loans" className="space-y-6 mt-6">
          <LoansTab />
        </TabsContent>

        {/* How Much Did I Buy Tab */}
        <TabsContent value="how-much-did-i-buy" className="space-y-6 mt-6">
          <HowMuchDidIBuyTab />
        </TabsContent>
      </Tabs>

      {/* Turnover Breakdown Diagnostic Modal */}
      <Dialog open={showTurnoverBreakdown} onOpenChange={setShowTurnoverBreakdown}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="turnover-breakdown-modal">
          <DialogHeader>
            <DialogTitle>Turnover Breakdown — {data?.period?.month_name || ""} {data?.period?.year || ""}</DialogTitle>
            <DialogDescription>
              Reconcile every Rand. Compare against your Excel totals to find any discrepancy.
            </DialogDescription>
          </DialogHeader>

          {loadingBreakdown && (
            <div className="py-12 text-center text-gray-500" data-testid="breakdown-loading">
              <RefreshCw className="h-6 w-6 animate-spin inline mr-2" />
              Loading breakdown…
            </div>
          )}

          {!loadingBreakdown && breakdownData?.error && (
            <div className="py-8 text-center text-red-600">{breakdownData.error}</div>
          )}

          {!loadingBreakdown && breakdownData && !breakdownData.error && (
            <div className="space-y-5 text-sm">
              {/* INCLUDED in turnover */}
              <section data-testid="breakdown-included">
                <h4 className="font-semibold text-green-700 uppercase text-xs mb-2">
                  Included in Turnover (by transaction type)
                </h4>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-green-50">
                      <tr>
                        <th className="text-left p-2">Type</th>
                        <th className="text-right p-2">Count</th>
                        <th className="text-right p-2">Total (incl. VAT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(breakdownData.included_by_type || []).map((row, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{row.transaction_type}</td>
                          <td className="p-2 text-right">{row.count}</td>
                          <td className="p-2 text-right font-mono">{formatCurrency(row.total_incl_vat)}</td>
                        </tr>
                      ))}
                      <tr className="border-t bg-green-50 font-semibold">
                        <td className="p-2">Sub-total — all positive sale lines</td>
                        <td></td>
                        <td className="p-2 text-right font-mono" data-testid="breakdown-included-total">
                          {formatCurrency(breakdownData.included_total_incl_vat)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Services now included in turnover */}
              <section data-testid="breakdown-services">
                <h4 className="font-semibold text-amber-700 uppercase text-xs mb-2">
                  Services Included (airtime / electricity / DSTV / vouchers)
                </h4>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-amber-50">
                      <tr>
                        <th className="text-left p-2">Service</th>
                        <th className="text-right p-2">Count</th>
                        <th className="text-right p-2">Total (incl. VAT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(breakdownData.services_included || breakdownData.services_subtracted || []).length === 0 ? (
                        <tr><td colSpan={3} className="p-3 text-center text-gray-400">No service line items in this period.</td></tr>
                      ) : (
                        (breakdownData.services_included || breakdownData.services_subtracted || []).map((row, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 capitalize">{row.kind}</td>
                            <td className="p-2 text-right">{row.count}</td>
                            <td className="p-2 text-right font-mono">{formatCurrency(row.total_incl_vat)}</td>
                          </tr>
                        ))
                      )}
                      <tr className="border-t bg-amber-50 font-semibold">
                        <td className="p-2">Sub-total — services in turnover</td>
                        <td></td>
                        <td className="p-2 text-right font-mono" data-testid="breakdown-services-total">
                          {formatCurrency(breakdownData.services_total_incl_vat)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Reconciliation */}
              <section className="bg-blue-50 border border-blue-200 rounded p-3" data-testid="breakdown-reconciliation">
                <h4 className="font-semibold text-blue-800 uppercase text-xs mb-2">Reconciliation</h4>
                <div className="space-y-1 font-mono text-sm">
                  <div className="flex justify-between">
                    <span>All positive sale lines (incl. VAT)</span>
                    <span>{formatCurrency(breakdownData.reconciliation?.all_positive_sale_lines_incl_vat)}</span>
                  </div>
                  <div className="flex justify-between text-amber-700">
                    <span>of which services (included)</span>
                    <span>{formatCurrency(breakdownData.reconciliation?.services_included_in_turnover ?? breakdownData.reconciliation?.minus_services_passthrough)}</span>
                  </div>
                  <div className="flex justify-between font-bold border-t pt-1 text-blue-900">
                    <span>= Financial Summary Turnover After Discount</span>
                    <span data-testid="breakdown-final-turnover">{formatCurrency(breakdownData.reconciliation?.equals_financial_summary_turnover_after_discount)}</span>
                  </div>
                </div>
                {breakdownData.reconciliation?.note && (
                  <p className="text-xs text-blue-700 mt-3 font-normal leading-relaxed">{breakdownData.reconciliation.note}</p>
                )}
              </section>

              {/* Voids excluded */}
              <section data-testid="breakdown-voids">
                <h4 className="font-semibold text-gray-700 uppercase text-xs mb-2">
                  Voided / Cancelled (not in turnover)
                </h4>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-2">Type</th>
                        <th className="text-right p-2">Count</th>
                        <th className="text-right p-2">Total (incl. VAT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(breakdownData.voids_excluded || []).length === 0 ? (
                        <tr><td colSpan={3} className="p-3 text-center text-gray-400">No voids/cancellations.</td></tr>
                      ) : (
                        (breakdownData.voids_excluded || []).map((row, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2">{row.transaction_type}</td>
                            <td className="p-2 text-right">{row.count}</td>
                            <td className="p-2 text-right font-mono">{formatCurrency(row.total_incl_vat)}</td>
                          </tr>
                        ))
                      )}
                      <tr className="border-t bg-gray-50 font-semibold">
                        <td className="p-2">Sub-total</td>
                        <td></td>
                        <td className="p-2 text-right font-mono" data-testid="breakdown-voids-total">
                          {formatCurrency(breakdownData.voids_total_incl_vat)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Refunds excluded */}
              <section data-testid="breakdown-refunds">
                <h4 className="font-semibold text-red-700 uppercase text-xs mb-2">
                  Refunds / Petty Cash (not in turnover)
                </h4>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-red-50">
                      <tr>
                        <th className="text-left p-2">Type</th>
                        <th className="text-right p-2">Count</th>
                        <th className="text-right p-2">Total (incl. VAT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(breakdownData.refunds_excluded || []).length === 0 ? (
                        <tr><td colSpan={3} className="p-3 text-center text-gray-400">No refunds in this period.</td></tr>
                      ) : (
                        (breakdownData.refunds_excluded || []).map((row, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2">{row.transaction_type}</td>
                            <td className="p-2 text-right">{row.count}</td>
                            <td className="p-2 text-right font-mono">{formatCurrency(row.total_incl_vat)}</td>
                          </tr>
                        ))
                      )}
                      <tr className="border-t bg-red-50 font-semibold">
                        <td className="p-2">Sub-total</td>
                        <td></td>
                        <td className="p-2 text-right font-mono" data-testid="breakdown-refunds-total">
                          {formatCurrency(breakdownData.refunds_total_incl_vat)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialSummary;
